import { v4 as uuidV4 } from "uuid";
import { CancelToken } from "axios";
import { isNil } from "lodash";
import { BizService } from "../explore/BizServiceCommon";
import { GetDimensionsRequesPayload, SaveOnboardingResponse, UseCaseDiscoveryResponse } from "../types";
import { logger } from "../../../core";
import {
  AddCohortRequest,
  AddCohortResponse,
  AddKnowledgeBaseRequest,
  AddKPIRequest,
  AddMemberRequestList,
  AddOp10zeRequest,
  AddOp10zeResponse,
  AddWidgetRequest,
  AddWidgetResponse,
  CohortGenerationRequest,
  CohortGenerationResponse,
  CopilotCreationInput,
  CopilotWorkflowOutput,
  CopilotWorkflowStatusInput,
  EditKnowledgeBaseRequest,
  EventTypeSettingsList,
  FeedCommandList,
  GetUseCaseFeedsRequest,
  GetUseCaseFeedsResponse,
  IndexSyncStatus,
  InsightDescription,
  InsightsSummaryGenerationRequest,
  InsightsSummaryGenerationResponse,
  KnowledgeBaseSettingsList,
  KnowledgeSummary,
  KPI,
  KPIDataQueryDefRequest,
  KPIInsightsRequest,
  KPIInsightsResponse,
  KpiRecommendationRequest,
  KpiRecommendationResponse,
  KpiRecommendationResponseStatusInput,
  KpisByUseCaseResponse,
  MemberSettings,
  RecommendedUseCase,
  SuggestDashboardsRequest,
  SuggestDashboardsResponse,
  SuggestDataQueryDefRequest,
  SuggestedDataQueryResponse,
  SuggestKPIRequest,
  SuggestKPIResponse,
  SuggestUsecasesResponseV2,
  UpdateEventTypeSettingsRequest,
  UpdateUseCaseMetadataRequest,
  UseCaseConfig,
  UseCaseConfigList,
  UseCaseDataQueryConfig,
  UseCaseFeedResponseList,
  UseCaseOp10zeQueryParams,
  UseCaseOp10zeSearchRequest,
  UseCaseOp10zeSearchResponse,
  UseCasePreviewResponse,
  UseCaseSaveRequest,
  UseCaseSaveResponse,
  UseCaseSchema,
  UseCaseSuggestionsRequest,
  UseCaseSuggestionSummaryRequest,
  UseCaseSuggestionSummaryResponse,
  UseCaseUnderlyingInfo,
  UsecaseUpsertResponse,
  VerticalIdentifier,
  WidgetDescription
} from "./types";

class UseCaseApiService extends BizService {
  getUseCaseSchema(id: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${id}`);

    const request = () => this.datasource.get<UseCaseSchema, null>(url);
    return this.getResult(request);
  }

  getUseCaseSummary(id: string) {
    this.init();

    const url = this.getBizEntityUrl(`useCase/${id}/schema`);

    const request = () => this.datasource.get<UseCasePreviewResponse>(url);
    return this.getResult(request);
  }

  generateDataQueryDef(
    verticalId: string,
    subVerticalId: string,
    widgetDescription: WidgetDescription,
    insightDescription: InsightDescription,
    useCaseName: string,
    companyName?: string,
    usecaseId?: string,
    kpiId?: string
  ) {
    this.init();

    const url = this.getBizEntityUrl("usecase/v2/generateDataQueryDef");
    const payload: SuggestDataQueryDefRequest = {
      subVerticalId,
      verticalId,
      companyName,
      useCaseName,
      usecaseId,
      kpiId
    };

    if (companyName) {
      payload.companyName = companyName;
    }

    if (widgetDescription) {
      payload.widgetDescription = widgetDescription;
    } else if (insightDescription) {
      payload.insightDescription = insightDescription;
    }

    const request = () => this.datasource.post<SuggestedDataQueryResponse, SuggestDataQueryDefRequest>(url, payload);
    return this.getResult(request);
  }

  fetchAllKpisByUseCase(companyName: string) {
    this.init();

    const url = this.getBizEntityUrl(`kpi`);
    const request = () =>
      this.datasource.get<KpisByUseCaseResponse, null>(url, null, {
        params: {
          companyName
        }
      });
    return this.getResult(request);
  }

  saveUseCase(verticalId: string, subVerticalId: string, useCaseConfig: UseCaseConfig, companyName?: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/v2/save`);
    const payload: UseCaseSaveRequest = {
      subVerticalId,
      verticalId,
      useCaseConfig
    };

    if (companyName) {
      payload.companyName = companyName;
    }

    if (payload?.useCaseConfig?.dataQueryConfigs) {
      const numDqcsBeforeCheck = payload.useCaseConfig.dataQueryConfigs.length;
      // Retain DataQueryConfig only if it has a valid opCreationConfig
      payload.useCaseConfig.dataQueryConfigs = payload.useCaseConfig.dataQueryConfigs.filter(
        dqc => dqc.relatedOpConfigs[0]?.bizDataOperationalize?.opCreationConfig
      );

      if (payload.useCaseConfig.dataQueryConfigs.length !== numDqcsBeforeCheck) {
        logger.warn("UseCaseApiService", "Found DQCs without valid opCreationConfig, removing them from payload");
      }
    }

    const request = () => this.datasource.post<UseCaseSaveResponse, UseCaseSaveRequest>(url, payload);
    return this.getResult(request);
  }

  updateUseCase(
    verticalId: string,
    subVerticalId: string,
    useCaseId: string,
    useCaseConfig: UseCaseConfig,
    companyName?: string
  ) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/v2/${useCaseId}`);
    const payload: UseCaseSaveRequest = {
      subVerticalId,
      verticalId,
      useCaseConfig
    };

    if (companyName) {
      payload.companyName = companyName;
    }

    if (payload?.useCaseConfig?.dataQueryConfigs) {
      const numDqcsBeforeCheck = payload.useCaseConfig.dataQueryConfigs.length;
      // Retain DataQueryConfig only if it has a valid opCreationConfig
      payload.useCaseConfig.dataQueryConfigs = payload.useCaseConfig.dataQueryConfigs.filter(
        dqc => dqc.relatedOpConfigs[0]?.bizDataOperationalize?.opCreationConfig
      );

      if (payload.useCaseConfig.dataQueryConfigs.length !== numDqcsBeforeCheck) {
        logger.warn("UseCaseApiService", "Found DQCs without valid opCreationConfig, removing them from payload");
      }
    }

    const request = () => this.datasource.post<UseCaseSaveResponse, UseCaseSaveRequest>(url, payload);
    return this.getResult(request);
  }

  syncKb(useCaseId: string) {
    this.init();

    const url = this.getBizEntityUrl(`index/sync`);
    const request = () =>
      this.datasource.post<IndexSyncStatus, any>(
        url,
        {},
        {
          params: {
            useCaseId
          }
        }
      );
    return this.getResult(request);
  }
  syncKbV2(useCaseId: string) {
    this.init();

    const url = this.getBizEntityUrl(`index/syncV2`);
    const request = () =>
      this.datasource.post<IndexSyncStatus, any>(
        url,
        {},
        {
          params: {
            useCaseId
          }
        }
      );
    return this.getResult(request);
  }

  getSyncKbStatus(useCaseId: string) {
    this.init();

    const url = this.getBizEntityUrl(`index/status`);
    const request = () =>
      this.datasource.get<IndexSyncStatus, any>(
        url,
        {},
        {
          params: {
            useCaseId
          }
        }
      );
    return this.getResult(request);
  }
  getSyncKbStatusV2(pollId: string) {
    this.init();

    const url = this.getBizEntityUrl(`index/statusV2`);
    const request = () =>
      this.datasource.get<IndexSyncStatus, any>(
        url,
        {},
        {
          params: {
            pollId
          }
        }
      );
    return this.getResult(request);
  }

  getKnowledgeSummary(useCaseId: string, companyName: string) {
    this.init();

    const url = this.getBizEntityUrl(`context/summary`);
    const request = () =>
      this.datasource.get<KnowledgeSummary, null>(url, null, {
        params: {
          useCaseId,
          companyName
        }
      });
    return this.getResult(request);
  }

  updateKnowledgeSummary(useCaseId: string, companyName: string, payload: KnowledgeSummary) {
    this.init();

    const url = this.getBizEntityUrl(`context/summary`);
    const request = () =>
      this.datasource.post<string, KnowledgeSummary>(url, payload, {
        params: {
          useCaseId,
          companyName
        }
      });
    return this.getResult(request);
  }

  deleteUseCase(useCaseId: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}`);
    const request = () => this.datasource.delete<UsecaseUpsertResponse>(url);
    return this.getResult(request);
  }

  updateUseCaseMeta(
    useCaseId: string,
    useCaseName?: string,
    useCaseDescription?: string,
    iconName?: string,
    subVertical?: string,
    isPrivate?: boolean
  ) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/metadata`);
    const payload: UpdateUseCaseMetadataRequest = {};

    if (useCaseName) {
      payload.name = useCaseName;
    }

    if (useCaseDescription) {
      payload.description = useCaseDescription;
    }

    if (iconName) {
      payload.icon = iconName;
    }

    if (subVertical) {
      payload.subVertical = subVertical;
    }

    if (!isNil(isPrivate)) {
      payload.isPrivate = isPrivate;
    }

    const request = () => this.datasource.post<UsecaseUpsertResponse, UpdateUseCaseMetadataRequest>(url, payload);
    return this.getResult(request);
  }

  getUseCasesForMember(companyName: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/member?companyName=${companyName}`);

    const request = () => this.datasource.get<UseCaseConfigList, never>(url);
    return this.getResult(request);
  }

  editKnowledgeBaseSettings(payload: EditKnowledgeBaseRequest, useCaseId: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/editKnowledgeBaseSettings`);
    const request = () => this.datasource.post<KnowledgeBaseSettingsList, EditKnowledgeBaseRequest>(url, payload);
    return this.getResult(request);
  }

  addMemberToUseCase(useCaseId: string, memberIds: string[]) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/addMembers`);
    const payload: AddMemberRequestList = {
      addMemberRequestList: (memberIds || []).map(memberId => ({ userId: memberId }))
    };

    const request = () => this.datasource.post<MemberSettings, AddMemberRequestList>(url, payload);
    return this.getResult(request);
  }

  removeMemberFromUseCase(useCaseId: string, memberId: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/removeMember/${memberId}`);

    const request = () => this.datasource.delete<UsecaseUpsertResponse>(url);
    return this.getResult(request);
  }

  addWidgetToUseCase(useCaseId: string, dataQueryConfig: UseCaseDataQueryConfig) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/addWidget`);

    const request = () => this.datasource.post<AddWidgetResponse, AddWidgetRequest>(url, { query: dataQueryConfig });
    return this.getResult(request);
  }

  removeWidgetFromUseCase(useCaseId: string, dataQueryConfigId: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/removeWidget/${dataQueryConfigId}`);

    const request = () => this.datasource.delete<UsecaseUpsertResponse>(url);
    return this.getResult(request);
  }

  addOp10zeToUseCase(useCaseId: string, opIds: string[]) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/addOp10ze`);

    const request = () => this.datasource.post<AddOp10zeResponse, AddOp10zeRequest>(url, { opIds });
    return this.getResult(request);
  }

  removeOp10zeFromUseCase(useCaseId: string, dataQueryConfigId: string, opId: string[]) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/removeOp10ze/${dataQueryConfigId}/${opId}`);

    const request = () => this.datasource.put<UsecaseUpsertResponse, never>(url);
    return this.getResult(request);
  }

  deleteOp10zeFromUseCase(useCaseId: string, dataQueryConfigId: string, opId: string[]) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/deleteOp10ze/${dataQueryConfigId}/${opId}`);

    const request = () => this.datasource.delete<UsecaseUpsertResponse>(url);
    return this.getResult(request);
  }

  getOp10zeForUseCases(useCaseIds: string[], companyName: string, queryParams?: UseCaseOp10zeQueryParams) {
    this.init();

    const url = this.getBizEntityUrl("usecase/v2/op10ze/search");
    const payload: UseCaseOp10zeSearchRequest = {
      useCaseIds,
      companyName
    };

    const request = () =>
      this.datasource.post<UseCaseOp10zeSearchResponse, UseCaseOp10zeSearchRequest>(url, payload, {
        params: queryParams
      });
    return this.getResult(request);
  }

  getEventSettings(useCaseId: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/eventSettings`);

    const request = () => this.datasource.get<EventTypeSettingsList, never>(url);
    return this.getResult(request);
  }

  updateEventSettings(useCaseId: string, eventTypeSettingsList: EventTypeSettingsList) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/eventSettings`);

    const request = () =>
      this.datasource.post<UsecaseUpsertResponse, UpdateEventTypeSettingsRequest>(url, {
        eventTypeSettingsList
      });
    return this.getResult(request);
  }

  getKnowledgeBaseSettings(useCaseId: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/knowledgeBaseSettings`);

    const request = () => this.datasource.get<KnowledgeBaseSettingsList, never>(url);
    return this.getResult(request);
  }

  addKnowledgeBaseSettings(useCaseId: string, requestPayload: AddKnowledgeBaseRequest) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/addKnowledgeBaseSettings`);

    const request = () => this.datasource.post<KnowledgeBaseSettingsList, AddKnowledgeBaseRequest>(url, requestPayload);
    return this.getResult(request);
  }

  deleteKnowledgeBaseSettings(useCaseId: string, knowledgeBaseId: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/knowledgeBaseSettings/${knowledgeBaseId}`);

    const request = () => this.datasource.delete<UsecaseUpsertResponse>(url);
    return this.getResult(request);
  }

  fetchKPIById(kpiId: string, useCaseId: string, companyName: string) {
    this.init();

    const url = this.getBizEntityUrl(`kpi/${kpiId}?useCaseId=${useCaseId}&companyName=${companyName}`);

    const request = () => this.datasource.get<KPI, never>(url);
    return this.getResult(request);
  }

  addKPIToUseCase(useCaseId: string, kpi: KPI) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/addKpi`);

    const request = () => this.datasource.post<UsecaseUpsertResponse, AddKPIRequest>(url, { kpi });
    return this.getResult(request);
  }

  updateKpiInUseCase(useCaseId: string, kpi: KPI) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/updateKpi/${kpi.id}`);

    const request = () => this.datasource.post<UsecaseUpsertResponse, AddKPIRequest>(url, { kpi });
    return this.getResult(request);
  }

  deleteKPIFromUseCase(useCaseId: string, kpiId: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/deleteKpi/${kpiId}`);

    const request = () => this.datasource.delete<UsecaseUpsertResponse, never>(url);
    return this.getResult(request);
  }

  discoverUseCase(useCaseId: string, companyName: string) {
    this.init();

    const subUrl = useCaseId
      ? `usecase/v2/discover/${useCaseId}`
      : `usecase/v2/discover/allUseCases?companyName=${companyName}`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () => this.datasource.get<UseCaseDiscoveryResponse, unknown>(url);
    return this.getResult(request);
  }

  getUseCaseSampleValues(useCaseId: string, payload: UseCaseDiscoveryResponse) {
    this.init();

    const subUrl = useCaseId
      ? `usecase/v2/discover/${useCaseId}/samplevalues`
      : `usecase/v2/discover/allUseCases/samplevalues`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () => this.datasource.post<UseCaseDiscoveryResponse, UseCaseDiscoveryResponse>(url, payload);
    return this.getResult(request);
  }

  connectRawData(
    companyName: string,
    eventTypes: UseCaseDiscoveryResponse["eventTypes"],
    entityTypes: UseCaseDiscoveryResponse["entityTypes"]
  ) {
    this.init();

    const url = `/event-mapping/api/v1/data-onboarding/connect/rawdata`;
    const payload = {
      companyName,
      eventTypes,
      entityTypes,
      traceInfo: {
        traceId: uuidV4()
      }
    };

    const request = () => this.datasource.post<SaveOnboardingResponse, any>(url, payload);
    return this.getResult(request);
  }

  getUnderlyingInfoForUseCase(
    useCaseId: string,
    includeEventTypeIds = true,
    includeEntityTypes = true,
    includeEventEntityMap = true,
    includeEntityTypesAsEvents = false
  ) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/underlyingInfo`);

    const request = () =>
      this.datasource.get<UseCaseUnderlyingInfo>(url, null as never, {
        params: {
          includeEventTypeIds,
          includeEntityTypes,
          includeEventEntityMap,
          includeEntityTypesAsEvents
        }
      });
    return this.getResult(request);
  }

  getUnderlyingEventTypesForUseCase(useCaseId: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/underlyingInfo/eventTypeIds`);

    const request = () => this.datasource.get<UseCaseUnderlyingInfo>(url);
    return this.getResult(request);
  }

  getFeedForUseCases(useCaseIds: string[], cancelToken?: CancelToken) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/user/feed`);

    const request = () =>
      this.datasource.post<GetUseCaseFeedsResponse, GetUseCaseFeedsRequest>(url, { useCaseIds }, { cancelToken });
    return this.getResult(request);
  }

  getFeedSummaryForUseCase(useCaseId: string, kpiIds: string[], startMillis: number, endMillis: number) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/feedSummary`);

    const payload: InsightsSummaryGenerationRequest = {
      useCaseId,
      kpiIds,
      timeRange: {
        startDateMillis: startMillis,
        endDateMillis: endMillis
      }
    };

    const request = () =>
      this.datasource.post<InsightsSummaryGenerationResponse, InsightsSummaryGenerationRequest>(url, payload);
    return this.getResult(request);
  }

  getFeedListForUseCase(useCaseId: string, cancelToken?: CancelToken, feedListType?: string) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/user/feedList`);

    const params: Record<string, string> = {
      ...(feedListType && { feedListType })
    };

    const request = () =>
      this.datasource.get<UseCaseFeedResponseList, null>(url, null, {
        params,
        cancelToken
      });
    return this.getResult(request);
  }

  getFeedListForUseCaseWithKpiIds(
    useCaseId: string,
    feedListType?: string,
    kpiIds?: string[],
    startTimeMillis?: number,
    endTimeMillis?: number
  ) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/user/feedList`);

    const request = () =>
      this.datasource.post<
        UseCaseFeedResponseList,
        { feedListType: string; kpiIds: string[]; startTimeMillis: number; endTimeMillis: number }
      >(url, {
        feedListType,
        kpiIds,
        startTimeMillis,
        endTimeMillis
      });
    return this.getResult(request);
  }

  getFeedCommandsForUseCase(useCaseId: string, cancelToken?: CancelToken) {
    this.init();

    const url = this.getBizEntityUrl(`usecase/${useCaseId}/user/feedCommands`);

    const request = () => this.datasource.get<FeedCommandList, null>(url, null, { cancelToken });
    return this.getResult(request);
  }

  getDimensions(useCaseId: string, payload: GetDimensionsRequesPayload) {
    this.init();
    const url = this.getBizEntityUrl(`usecase/v2/discover/${useCaseId}/addDimensions`);
    const request = () => this.datasource.get<UseCaseDiscoveryResponse, GetDimensionsRequesPayload>(url, payload);
    return this.getResult(request);
  }

  addMemory(useCaseId: string, memory: string) {
    this.init();

    const url = this.getBizEntityUrl(`${useCaseId}/addToMemory`);
    const body = JSON.stringify({ text: memory }); // <-- send raw JSON string

    /* POST<responseType, requestBodyType>(...) */
    const request = () =>
      this.datasource.post<any, string>(url, body, {
        headers: { "Content-Type": "application/json" } // <-- explicit JSON header
      });

    return this.getResult(request);
  }
  updateMemory(useCaseId: string, memoryId: string, memory: string) {
    this.init();

    const url = this.getBizEntityUrl(`${useCaseId}/memory/${memoryId}/updateMemory`);
    const body = JSON.stringify({ text: memory });

    const request = () =>
      this.datasource.post<any, string>(url, body, {
        headers: { "Content-Type": "application/json" }
      });

    return this.getResult(request);
  }
  deleteMemory(useCaseId: string, memoryId: string) {
    this.init();

    const url = this.getBizEntityUrl(`${useCaseId}/memory/${memoryId}/deleteMemory`);

    /* Send an empty JSON object so Content-Type is application/json */
    const body = "{}";

    const request = () =>
      this.datasource.post<any, string>(url, body, {
        headers: { "Content-Type": "application/json" }
      });

    return this.getResult(request);
  }

  getSuggestedUseCases(companyName: string, verticalId: string, subVerticalId: string, chatId: string) {
    this.init();

    const subUrl = `usecase/v2/suggest`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () =>
      this.datasource.post<SuggestUsecasesResponseV2, UseCaseSuggestionsRequest>(url, {
        chatId,
        verticalIdentifier: {
          companyName,
          subVertical: subVerticalId,
          vertical: verticalId
        }
      });
    return this.getResult(request);
  }

  getUseCaseSummaryForSuggestion(suggestion: RecommendedUseCase) {
    this.init();

    const subUrl = `usecase/v2/suggest/summary`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () =>
      this.datasource.post<UseCaseSuggestionSummaryResponse, UseCaseSuggestionSummaryRequest>(url, {
        useCaseRecommendation: suggestion
      });
    return this.getResult(request);
  }

  getCohort(usecaseId: string, cohortCount: number, verticalIdentifier: VerticalIdentifier, requestedCohortName = "") {
    this.init();

    const subUrl = `usecase/v2/cohortStart`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () =>
      this.datasource.post<CohortGenerationResponse, CohortGenerationRequest>(url, {
        verticalIdentifier,
        cohortCount,
        usecaseId,
        requestedCohortName
      });
    return this.getResult(request);
  }

  getCohortStatus(pollId: string) {
    this.init();

    const subUrl = `usecase/v2/cohortStatus`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () =>
      this.datasource.post<CohortGenerationResponse, unknown>(
        url,
        {},
        {
          params: { pollId }
        }
      );
    return this.getResult(request);
  }

  addCohort(payload: AddCohortRequest) {
    this.init();

    const subUrl = `usecase/v2/addCohort`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () => this.datasource.post<AddCohortResponse, AddCohortRequest>(url, payload);
    return this.getResult(request);
  }

  fetchUseCaseRecommendation(
    runId: string,
    companyName: string,
    verticalIds: VerticalIdentifier[],
    inputContext?: string
  ) {
    this.init();
    const subUrl = `usecase/v2/creation`;
    const url = this.getBizEntityUrl(subUrl);
    const payload: CopilotCreationInput = {
      runId,
      companyName,
      verticalIdentifiers: verticalIds,
      isD2C: true,
      inputContext: inputContext || ""
    };
    const request = () => this.datasource.post<CopilotWorkflowOutput, CopilotCreationInput>(url, payload);
    return this.getResult(request);
  }

  fetchUseCaseRecommendationStatus(runId: string) {
    this.init();
    const subUrl = `usecase/v2/status`;
    const url = this.getBizEntityUrl(subUrl);
    const request = () => this.datasource.post<CopilotWorkflowOutput, CopilotWorkflowStatusInput>(url, { runId });
    return this.getResult(request);
  }

  getSuggestedKPIs(
    useCaseConfig: UseCaseConfig,
    companyName: string,
    verticalId: string,
    subVerticalId: string,
    chatId: string
  ) {
    this.init();

    const subUrl = `usecase/v2/suggest/kpis`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () =>
      this.datasource.post<SuggestKPIResponse, SuggestKPIRequest>(url, {
        companyName,
        chatId,
        subVerticalId,
        verticalId,
        useCaseConfig
      });
    return this.getResult(request);
  }

  getSuggestedKPIsV2(useCaseConfigKpiRecommendationRequest: KpiRecommendationRequest) {
    this.init();

    const subUrl = `usecase/v2/createKPI`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () =>
      this.datasource.post<KpiRecommendationResponse, KpiRecommendationRequest>(
        url,
        useCaseConfigKpiRecommendationRequest
      );
    return this.getResult(request);
  }

  getSuggestedKPIsV2Status(runId: string) {
    this.init();

    const subUrl = `usecase/v2/createKPIStatus`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () =>
      this.datasource.post<KpiRecommendationResponse, KpiRecommendationResponseStatusInput>(url, { runId });
    return this.getResult(request);
  }

  getSuggestedDashboards(
    useCaseConfig: UseCaseConfig,
    chatId: string,
    verticalId: string,
    subVerticalId: string,
    companyName: string,
    numberOfDashboards: number
  ) {
    this.init();

    const subUrl = `usecase/v2/suggest/dashboards`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () =>
      this.datasource.post<SuggestDashboardsResponse, SuggestDashboardsRequest>(url, {
        useCaseConfig,
        chatId,
        verticalId,
        subVerticalId,
        companyName,
        numberOfDashboards
      });
    return this.getResult(request);
  }

  generateKPIDataQueryDef(
    kpiName: string,
    useCaseName: string,
    companyName: string,
    verticalId: string,
    subVerticalId: string,
    useCaseId?: string,
    useCaseConfig?: UseCaseConfig
  ) {
    this.init();
    const url = this.getBizEntityUrl("usecase/v2/generateKPIQueryDef");

    const request = () =>
      this.datasource.post<SuggestKPIResponse, KPIDataQueryDefRequest>(url, {
        kpiName,
        useCaseName,
        verticalIdentifier: {
          companyName,
          subVertical: subVerticalId,
          vertical: verticalId
        },
        useCaseId,
        useCaseConfig
      });
    return this.getResult(request);
  }

  generateInsightsForKPI(
    kpiName: string,
    kpiDescription: string,
    useCaseName: string,
    useCaseDescription: string,
    involvedEvents: string[],
    companyName: string,
    verticalId: string,
    subVerticalId: string,
    useCaseId?: string,
    useCaseConfig?: UseCaseConfig,
    insightsCount = 1,
    kpiId = ""
  ) {
    this.init();

    const subUrl = `usecase/v2/generateInsights`;
    const url = this.getBizEntityUrl(subUrl);

    const request = () =>
      this.datasource.post<KPIInsightsResponse, KPIInsightsRequest>(url, {
        kpiName,
        kpiDescription,
        useCaseName,
        useCaseDescription,
        involvedEvents,
        insightsCount,
        verticalIdentifier: {
          companyName,
          subVertical: subVerticalId,
          vertical: verticalId
        },
        useCaseId,
        useCaseConfig,
        kpiId
      });
    return this.getResult(request);
  }
}

export const useCaseApiService = new UseCaseApiService();
